import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Button, Intent } from '@blueprintjs/core';
import * as EmailValidator from 'email-validator';
import FormDrawer from 'components/FormDrawer';
import { FormSection, FieldError, Toggle } from 'components/FormBits';
import SkillsPicker from 'components/SkillsPicker';

// What a user can do in the mobile app, in the order shown on their page and
// here. `key` is the user attribute; the server sends true/false or "true"/"false".
export const MOBILE_PERMISSIONS = [
  { key: 'enable_invoices_on_mobile', label: 'Create invoices' },
  { key: 'enable_display_unit_prices', label: 'See unit prices' },
  { key: 'enable_invoice_sent_link', label: 'Send invoice links' },
  { key: 'enable_estimate_sent_link', label: 'Send estimate links' },
  { key: 'enable_work_report_sent_link', label: 'Send work order links' },
  { key: 'enable_quickbooks_sync', label: 'Sync with QuickBooks' },
];

export const isOn = (value) => value === true || value === 'true';

const TEXT_FIELDS = ['first_name', 'last_name', 'phone', 'email', 'title'];

// Field -> input id, in the order they appear (the first with a problem gets focus).
const FIELD_IDS = {
  first_name: 'user-first-name',
  last_name: 'user-last-name',
  phone: 'user-phone',
  email: 'user-email',
};

const blank = (value) => !value || !String(value).trim();

// Edit a user's details (pages/users/show.js), in a side panel. Works on a
// copy: Cancel leaves the user as it was; Save sends the copy.
@inject('userStore') @observer
class EditUserPanel extends Component {
  @observable draft = {};
  @observable errors = {};

  componentDidMount() {
    if (this.props.isOpen) this.reset();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) this.reset();
  }

  reset() {
    const { attributes } = this.props.user;
    const draft = { skills: (attributes.skills || []).slice() };
    TEXT_FIELDS.forEach((key) => { draft[key] = attributes[key] || ''; });
    MOBILE_PERMISSIONS.forEach(({ key }) => { draft[key] = isOn(attributes[key]); });
    this.draft = draft;
    this.errors = {};
  }

  setField(key, value) {
    this.draft[key] = value;
    if (this.errors[key]) {
      this.errors = { ...this.errors, [key]: null };
    }
  }

  validate() {
    const { draft } = this;
    const errors = {};
    if (blank(draft.first_name)) errors.first_name = 'Enter a first name.';
    if (blank(draft.last_name)) errors.last_name = 'Enter a last name.';
    if (blank(draft.phone)) errors.phone = 'Enter a phone number.';
    if (!EmailValidator.validate(String(draft.email).trim())) errors.email = 'Enter an email address, like name@example.com.';
    return errors;
  }

  save = () => {
    const { user, userStore, onClose } = this.props;
    if (userStore.isSaving) return;
    const errors = this.validate();
    this.errors = errors;
    const first = Object.keys(FIELD_IDS).find((key) => errors[key]);
    if (first) {
      const element = document.getElementById(FIELD_IDS[first]);
      if (element) element.focus();
      return;
    }
    const attributes = { ...user.attributes, ...this.draft };
    TEXT_FIELDS.forEach((key) => { attributes[key] = String(attributes[key]).trim(); });
    userStore
      .updateUser({ id: user.id, attributes })
      .then(() => onClose())
      .catch(() => undefined); // the store shows why it failed; the panel stays open
  };

  textField(key, label, { required, type = 'text', placeholder, autoComplete } = {}) {
    const error = this.errors[key];
    const id = FIELD_IDS[key] || `user-${key.replace(/_/g, '-')}`;
    return (
      <div className={`bp3-form-group${error ? ' has-error' : ''}`}>
        <label className="fb-label" htmlFor={id}>
          {label}
          {required && <span className="fb-required" aria-hidden="true"> *</span>}
        </label>
        <input
          id={id}
          type={type}
          className="bp3-input bp3-fill"
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-required={required ? true : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          value={this.draft[key] || ''}
          onChange={(event) => this.setField(key, event.target.value)}
        />
        <FieldError id={`${id}-error`}>{error}</FieldError>
      </div>
    );
  }

  render() {
    const { user, isOpen, onClose, userStore } = this.props;
    const busy = Boolean(userStore.isSaving);
    const firstName = user.attributes.first_name || 'this user';
    return (
      <FormDrawer
        title="Edit details"
        icon="edit"
        tone="team"
        size="min(760px, 100vw)"
        isOpen={isOpen}
        onClose={onClose}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={onClose} disabled={busy} />
            <Button intent={Intent.PRIMARY} icon="floppy-disk" text="Save changes" loading={busy} onClick={this.save} />
          </>
        }
      >
        <FormSection title="Name and contact" hint="How the team and customers reach them.">
          <div className="fb-grid">
            {this.textField('first_name', 'First name', { required: true, autoComplete: 'given-name' })}
            {this.textField('last_name', 'Last name', { required: true, autoComplete: 'family-name' })}
            {this.textField('phone', 'Phone', { required: true, type: 'tel', placeholder: '000-000-0000', autoComplete: 'tel' })}
            {this.textField('email', 'Email', { required: true, type: 'email', placeholder: 'name@example.com', autoComplete: 'email' })}
          </div>
        </FormSection>

        <FormSection title="Their work" hint="Shown when picking technicians for a job.">
          {this.textField('title', 'Title', { placeholder: 'e.g. Master Technician' })}
          <div className="bp3-form-group">
            <span className="fb-label">Skills</span>
            <SkillsPicker
              placeholder="e.g. HVAC, Plumbing"
              skills={(this.draft.skills || []).slice()}
              onChange={(skills) => {
                this.draft.skills = skills.map((skill) => String(skill).trim()).filter((skill) => skill.length > 0);
              }}
            />
            <p className="bp3-form-helper-text">Pick from existing skills or type a new one.</p>
          </div>
        </FormSection>

        <FormSection title="Mobile app" hint={`What ${firstName} can do in the Field Nexus app.`}>
          {MOBILE_PERMISSIONS.map(({ key, label }) => (
            <Toggle
              key={key}
              id={`user-${key.replace(/_/g, '-')}`}
              label={label}
              checked={Boolean(this.draft[key])}
              onChange={(checked) => this.setField(key, checked)}
            />
          ))}
        </FormSection>
      </FormDrawer>
    );
  }
}

export default EditUserPanel;
